// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { dealerSelectors, type ReduxNext } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import * as configSelectors from '../selectors/config';
import * as proposalSelectors from '../selectors/proposalSelectors';
import { getCustomizablePdfPayload } from '../selectors/printPdfDealSelectors';
import {
    COPY_PROPOSAL,
    COPY_PROPOSAL_SUCCESS,
    CREATE_PROPOSAL,
    DELETE_PROPOSAL,
    DELETE_PROPOSAL_SUCCESS,
    GET_PROPOSAL,
    GET_PROPOSAL_LIST,
    GET_PROPOSAL_PREVIEW,
    GET_PROPOSAL_PREVIEW_FAILURE,
    GET_PROPOSAL_PREVIEW_SUCCESS,
    SET_IS_PRIMARY_PROPOSAL,
    UPDATE_PROPOSAL,
    UPDATE_PROPOSAL_SUCCESS
} from '../actionTypes/proposalActionTypes';
import {
    copyProposalRSAA,
    createProposalRSAA,
    deleteProposalRSAA,
    getProposalListRSAA,
    getProposalPreviewRSAA,
    getProposalRSAA,
    updateProposalRSAA
} from '../actions/proposalActionCreators';
import { ensurePrintingLogo } from '../services/ensurePrintingLogo';
import type { ScopedStore } from '@makemydeal/shared-scoped-store';

let proposalPreviewStartTime: number | null = null;
let proposalPreviewHasProposalId = false;
let proposalPreviewIsFourSquare = false;

export const getProposalList = (store: any, proposalType?: string) => {
    const state = store.getState();

    const dealerId = dealerSelectors.getDealerId(state);
    const bffEndpoint = configSelectors.getServicesBff(state);

    const getProposalListAction = getProposalListRSAA(dealerId, bffEndpoint, proposalType);

    store.dispatch(getProposalListAction);
};

export const middleware = (store: any) => (next: ReduxNext) => async (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case GET_PROPOSAL_LIST: {
            getProposalList(store, action.payload?.proposalType);
            break;
        }

        case GET_PROPOSAL_PREVIEW: {
            // MUST run before the payload is built: `dealerLogo` is read from state, not passed in.
            await ensurePrintingLogo(store);

            const state = store.getState();
            const proposalId = action.payload.proposalId;
            const selectedScopeIds = action.payload.selectedScopeIds;
            const scopeGridSelections = action.payload.scopeGridSelections;
            const proposals = proposalSelectors.getProposalList(state);
            const isFourSquare = proposals.some((p) => p.id === proposalId && p.type === 'fourSquare');

            proposalPreviewStartTime = performance.now();
            proposalPreviewHasProposalId = !!proposalId;
            proposalPreviewIsFourSquare = isFourSquare;

            store.dispatch(
                getProposalPreviewRSAA(
                    {
                        ...getCustomizablePdfPayload((store as ScopedStore).getRealState(), selectedScopeIds, scopeGridSelections),
                        proposalId
                    },
                    configSelectors.getServicesBff(state)
                )
            );
            break;
        }

        case GET_PROPOSAL_PREVIEW_SUCCESS: {
            if (proposalPreviewStartTime !== null) {
                logNewRelicPageAction('MV:PrintProposalPreviewCompleted', {
                    durationMs: performance.now() - proposalPreviewStartTime,
                    result: 'success',
                    hasProposalId: proposalPreviewHasProposalId,
                    isFourSquare: proposalPreviewIsFourSquare
                });
                proposalPreviewStartTime = null;
            }
            break;
        }

        case GET_PROPOSAL_PREVIEW_FAILURE: {
            if (proposalPreviewStartTime !== null) {
                logNewRelicPageAction('MV:PrintProposalPreviewCompleted', {
                    durationMs: performance.now() - proposalPreviewStartTime,
                    result: 'error',
                    hasProposalId: proposalPreviewHasProposalId,
                    isFourSquare: proposalPreviewIsFourSquare
                });
                proposalPreviewStartTime = null;
            }
            break;
        }
        case CREATE_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const createProposalAction = createProposalRSAA(action.payload.proposalData, bffEndpoint, dealerId);

            store.dispatch(createProposalAction);
            break;
        }
        case DELETE_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const deleteProposalAction = deleteProposalRSAA(bffEndpoint, dealerId, action.payload.proposalId);

            store.dispatch(deleteProposalAction);
            break;
        }
        case DELETE_PROPOSAL_SUCCESS: {
            getProposalList(store);
            break;
        }
        case COPY_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const copyProposalAction = copyProposalRSAA(bffEndpoint, dealerId, action.payload.proposalId);

            store.dispatch(copyProposalAction);
            break;
        }
        case COPY_PROPOSAL_SUCCESS: {
            getProposalList(store);
            break;
        }
        case SET_IS_PRIMARY_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const updateProposalAction = updateProposalRSAA({ isPrimary: true }, bffEndpoint, dealerId, action.payload.proposalId, {
                withListFetch: true
            });

            store.dispatch(updateProposalAction);
            break;
        }
        case UPDATE_PROPOSAL_SUCCESS: {
            if (action.meta?.withListFetch) {
                getProposalList(store);
            }
            break;
        }
        case GET_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const getProposalAction = getProposalRSAA(dealerId, action.payload.proposalId, bffEndpoint);

            store.dispatch(getProposalAction);
            break;
        }
        case UPDATE_PROPOSAL: {
            const state = store.getState();
            const dealerId = dealerSelectors.getDealerId(state);
            const bffEndpoint = configSelectors.getServicesBff(state);

            const createProposalAction = updateProposalRSAA(
                action.payload.proposalData,
                bffEndpoint,
                dealerId,
                action.payload.proposalId
            );

            store.dispatch(createProposalAction);
            break;
        }
        default:
            break;
    }
};
